// Répertoire des fournisseurs et procédures paramétrées (M15, lot L4-C, D-147) : recherche, code généré,
// catégories, prestataire du lot L3 rattaché, note moyenne des évaluations de fin de contrat ; procédures
// avec leurs étapes, pièces et commission (aucun seuil prérempli).
import type { Fournisseur } from '@cc/contrats';
import { Bouton, Champ, EtatVide, Tableau, type ColonneTableau } from '@cc/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useFournisseurs, useProcedures } from './api';
import { FormulaireFournisseur, FormulaireProcedure } from './FormulairesConsultation';
import { NavigationAchats } from './NavigationAchats';
import styles from './achats.module.css';

export function PageFournisseurs() {
  const { contexte } = useSession();
  const [recherche, setRecherche] = useState('');
  const fournisseurs = useFournisseurs(recherche.trim().length >= 2 ? recherche.trim() : undefined);
  const procedures = useProcedures();
  const [dialogue, setDialogue] = useState<'fournisseur' | 'procedure' | null>(null);
  const modifier = peut(contexte, 'achat', 'modifier');
  const colonnes: ColonneTableau<Fournisseur>[] = [
    { id: 'code', entete: 'Code', enTeteLigne: true, masquable: false, cellule: (f) => <span className="cc-mono">{f.code}</span>, valeurTri: (f) => f.code },
    { id: 'nom', entete: 'Raison sociale', cellule: (f) => f.nom, valeurTri: (f) => f.nom },
    { id: 'categories', entete: 'Catégories', cellule: (f) => f.categories.join(', ') },
    { id: 'contact', entete: 'Contact', cellule: (f) => [f.contact, f.telephone].filter(Boolean).join(' · ') },
    { id: 'prestataire', entete: 'Prestataire lié', cellule: (f) => f.prestataire?.nom ?? '' },
    { id: 'note', entete: 'Note moyenne', numerique: true, cellule: (f) => (f.note_moyenne === null ? 'non évalué' : `${f.note_moyenne.toLocaleString('fr-FR')} / 5 (${f.evaluations})`), valeurTri: (f) => f.note_moyenne ?? -1 },
    { id: 'contrats', entete: 'Contrats', numerique: true, cellule: (f) => f.contrats },
    { id: 'actif', entete: 'Actif', cellule: (f) => (f.actif ? 'Oui' : 'Non') },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Marchés publics et fournisseurs"
        titre="Fournisseurs et procédures"
        description="Répertoire des fournisseurs de la collectivité, évalués en fin de contrat, et procédures d'achat paramétrées."
        actions={modifier ? <Bouton variante="principal" icone={Plus} onClick={() => setDialogue('fournisseur')}>Nouveau fournisseur</Bouton> : null}
      />
      <NavigationAchats actif="fournisseurs" />
      <Champ libelle="Rechercher (nom, code, catégorie)" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
      <EtatRequete chargement={fournisseurs.isPending} erreur={fournisseurs.error} onReessayer={() => void fournisseurs.refetch()} lot="L4-C" fonction="Le répertoire des fournisseurs">
        <section className={styles.bloc} aria-labelledby="titre-fournisseurs">
          <h2 id="titre-fournisseurs">Fournisseurs</h2>
          {(fournisseurs.data?.elements ?? []).length === 0 ? <EtatVide titre="Aucun fournisseur"><p>Aucun fournisseur ne correspond.</p></EtatVide> : <Tableau legende="Fournisseurs" colonnes={colonnes} lignes={fournisseurs.data?.elements ?? []} cleLigne={(f) => f.id} cleColonnes="achats-fournisseurs" />}
        </section>
      </EtatRequete>
      <section className={styles.bloc} aria-labelledby="titre-procedures">
        <h2 id="titre-procedures">Procédures d&apos;achat paramétrées</h2>
        <p className={styles.detail}>Aucun seuil ni règle de la commande publique n&apos;est prérempli : ils restent à confirmer par l&apos;administration.</p>
        {modifier ? <div className={styles.actes}><Bouton variante="secondaire" icone={Plus} onClick={() => setDialogue('procedure')}>Paramétrer une procédure</Bouton></div> : null}
        {(procedures.data?.elements ?? []).length === 0 ? <p className={styles.detail}>Aucune procédure paramétrée.</p> : (
          <ul className={styles.liste}>
            {(procedures.data?.elements ?? []).map((p) => (
              <li key={p.id}>
                <strong>{p.libelle}</strong> ({p.code}{p.active ? '' : ', désactivée'}) : {p.etapes.map((e) => e.libelle).join(', ')}
                {p.pieces_exigees.length > 0 ? `. Pièces exigées : ${p.pieces_exigees.join(', ')}` : ''}
                {p.commission ? `. Commission : ${p.commission}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>
      {dialogue === 'fournisseur' ? <FormulaireFournisseur onFermer={() => setDialogue(null)} /> : null}
      {dialogue === 'procedure' ? <FormulaireProcedure onFermer={() => setDialogue(null)} /> : null}
    </>
  );
}
